import { clsx, type ClassValue } from 'clsx'

export function cn(...inputs: ClassValue[]) {
  return clsx(inputs)
}

export function formatTime(date: Date): string {
  return new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  }).format(date)
}

export function formatDate(date: Date): string {
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  }).format(date)
}

export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout | null = null
  
  return (...args: Parameters<T>) => {
    if (timeout) clearTimeout(timeout)
    timeout = setTimeout(() => func(...args), wait)
  }
}

export function throttle<T extends (...args: any[]) => any>(
  func: T,
  limit: number
): (...args: Parameters<T>) => void {
  let inThrottle: boolean = false
  
  return (...args: Parameters<T>) => {
    if (!inThrottle) {
      func(...args)
      inThrottle = true
      setTimeout(() => (inThrottle = false), limit)
    }
  }
}

// Accessibility helpers
export function getContrastColor(bgColor: string): 'light' | 'dark' {
  // Simple contrast calculation - in production, use a proper color contrast library
  const hex = bgColor.replace('#', '')
  const r = parseInt(hex.substr(0, 2), 16)
  const g = parseInt(hex.substr(2, 2), 16)
  const b = parseInt(hex.substr(4, 2), 16)
  const brightness = (r * 299 + g * 587 + b * 114) / 1000
  return brightness > 155 ? 'dark' : 'light'
}

// Outdoor/carnival optimized utilities
export function getReadabilityClass(priority: 'low' | 'medium' | 'high' = 'medium'): string {
  const baseClasses = 'outdoor-contrast'
  
  switch (priority) {
    case 'high':
      return `${baseClasses} font-bold text-shadow-lg`
    case 'medium':
      return `${baseClasses} font-medium`
    case 'low':
    default:
      return baseClasses
  }
}

// Device detection for mobile-first optimizations
export function isMobile(): boolean {
  if (typeof window === 'undefined') return false
  return window.innerWidth < 768
}

export function isTouchDevice(): boolean {
  if (typeof window === 'undefined') return false
  return 'ontouchstart' in window || navigator.maxTouchPoints > 0
}

// Sound/vibration for carnival environment
export function playNotificationSound(type: 'message' | 'error' | 'success' = 'message') {
  if ('AudioContext' in window) {
    // Simple audio feedback for outdoor environments
    const audioContext = new AudioContext()
    const oscillator = audioContext.createOscillator()
    const gainNode = audioContext.createGain()
    
    oscillator.connect(gainNode)
    gainNode.connect(audioContext.destination)
    
    const frequencies = {
      message: 800,
      error: 400,
      success: 1200
    }
    
    oscillator.frequency.setValueAtTime(frequencies[type], audioContext.currentTime)
    oscillator.type = 'sine'
    
    gainNode.gain.setValueAtTime(0, audioContext.currentTime)
    gainNode.gain.linearRampToValueAtTime(0.1, audioContext.currentTime + 0.01)
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.2)
    
    oscillator.start(audioContext.currentTime)
    oscillator.stop(audioContext.currentTime + 0.2)
  }
}

export function vibrate(pattern: number | number[] = 100) {
  if ('navigator' in window && 'vibrate' in navigator) {
    navigator.vibrate(pattern)
  }
}